'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useToast } from '@/lib/context/ToastContext';
import { useAuth } from '@/lib/context/AuthContext';
import { useSpecialties } from '@/lib/swr';
import { clearAuthSession } from '@/lib/services/auth';
import { Modal } from '@/components/ui/Modal';
import OfflineReserveModal from '@/components/payment/OfflineReserveModal';
import RegistrationForm, { type RegistrationFormProps } from '@/components/registration/RegistrationForm';
import {
  fetchRegistrationNeeds,
  needsForm,
  postRegistration,
  registrationErrorKey,
  type NeedsResult,
  type RegistrationNeeds,
  type RegistrationResponse,
  type SiteCheckout,
} from '@/lib/registration/client';
import type { QuestionAnswer } from '@/lib/tickets/questions';

export interface SiteRegistrationTarget { ticketId: string | null; promocodeId: string | null; isFree: boolean } // isFree: label only
export interface SiteRegistrationOptions {
  contentId: string | null;
  /** Toast on 'registered'. */
  successKey: string;
  onRegistered: (r: { enrollmentId: string | null; offline: boolean }) => void;
  onCheckout: (checkout: SiteCheckout) => void;
  onFormShown?: () => void;
  /** The flow ended without registering (cancel, refusal, error). */
  onAbort: () => void;
}

type ServerErrors = NonNullable<RegistrationFormProps['serverErrors']>;
interface OfflineOffer { amount: number; currency: string; instructions: string; answers: QuestionAnswer[] }

/**
 * The site's registration flow: ask what this registration still needs, show
 * the one participant form only when something is missing, post once, and
 * route every outcome. The page renders `element` once and calls `start`.
 */
export function useSiteRegistration(opts: SiteRegistrationOptions) {
  const { t, language } = useLanguage();
  const toast = useToast();
  const { user, refreshProfile } = useAuth();
  const specialties = useSpecialties();
  // `start` resolves after awaits: read the page's latest callbacks, not the ones it was called with.
  const optsRef = useRef(opts);
  useEffect(() => { optsRef.current = opts; });
  const targetRef = useRef<SiteRegistrationTarget | null>(null);
  const needsRef = useRef<RegistrationNeeds | null>(null);

  // State twin of targetRef for rendering the form.
  const [target, setTarget] = useState<SiteRegistrationTarget | null>(null);
  const [formNeeds, setFormNeeds] = useState<RegistrationNeeds | null>(null);
  // Set once per server refusal, so the form re-marks fields only when the server spoke again.
  const [serverErrors, setServerErrors] = useState<ServerErrors | null>(null);
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState<OfflineOffer | null>(null);
  const [reserving, setReserving] = useState(false);

  const close = () => {
    setFormNeeds(null);
    setServerErrors(null);
    setOffer(null);
  };

  const abort = (messageKey: string) => {
    close();
    toast.error(t(messageKey));
    optsRef.current.onAbort();
  };

  const loadNeeds = async (): Promise<NeedsResult> => {
    const contentId = optsRef.current.contentId;
    if (!contentId) return { ok: false, status: 500 };
    const result = await fetchRegistrationNeeds({ channel: 'site', contentId, ticketId: targetRef.current?.ticketId ?? null });
    if (result.ok) needsRef.current = result.needs;
    return result;
  };

  const needsFailed = (status: number) => {
    close();
    if (status === 401) {
      toast.error(t('authModal.authSessionRestoreFailed'), {
        duration: 15000,
        action: { label: t('authModal.authClearSessionAndRetry'), onClick: () => { void clearAuthSession().finally(() => window.location.reload()) } },
      });
    } else {
      toast.error(t(status === 0 ? 'courseDetails.networkErrorCheckConnection' : 'courseDetails.serverErrorTryAgain'));
    }
    optsRef.current.onAbort();
  };

  const showForm = (needs: RegistrationNeeds, errors: ServerErrors | null) => {
    const wasOpen = formNeeds !== null;
    setOffer(null);
    setFormNeeds(needs);
    setServerErrors(errors);
    if (!wasOpen) optsRef.current.onFormShown?.();
  };

  const handle = async (response: RegistrationResponse, answers: QuestionAnswer[], reserveOffline: boolean) => {
    const { successKey, onRegistered, onCheckout } = optsRef.current;
    switch (response.kind) {
      case 'registered':
        close();
        toast.success(t(successKey));
        onRegistered({ enrollmentId: response.enrollmentId, offline: false });
        return;
      case 'offline_reserved':
        close();
        toast.success(t('offlinePayment.reserveSuccess'));
        onRegistered({ enrollmentId: response.enrollmentId, offline: true });
        return;
      case 'offline_offer':
        setFormNeeds(null);
        setServerErrors(null);
        setOffer({ amount: response.amount, currency: response.currency, instructions: response.instructions, answers });
        return;
      case 'checkout':
        close();
        onCheckout(response.checkout as unknown as SiteCheckout);
        return;
    }
    switch (response.errorType) {
      case 'PROFILE_INCOMPLETE': {
        const fresh = await loadNeeds();
        // The form marks the named fields itself, so the needs it already has will do.
        const current = fresh.ok ? fresh.needs : formNeeds ?? needsRef.current;
        if (current) showForm(current, { missingFields: response.missingFields ?? [] });
        else if (!fresh.ok) needsFailed(fresh.status);
        return;
      }
      case 'ANSWERS_INVALID': {
        const current = formNeeds ?? needsRef.current;
        if (current) {
          showForm(current, { answerErrors: response.answerErrors ?? {} });
          return;
        }
        break;
      }
      case 'NETWORK':
        // Keep what was typed, or the offer being confirmed; the participant
        // can try again once back online.
        if (formNeeds || offer) {
          toast.error(t('courseDetails.networkErrorCheckConnection'));
          return;
        }
        break;
      case 'ANSWERS_NOT_STORED':
        // The enrollment exists; only the answers failed.
        close();
        toast.error(t('courseDetails.answersNotStoredError'));
        onRegistered({ enrollmentId: response.enrollmentId ?? null, offline: reserveOffline });
        return;
    }
    abort(registrationErrorKey(response.errorType, 'site'));
  };

  const submit = async (answers: QuestionAnswer[], reserveOffline = false) => {
    const contentId = optsRef.current.contentId;
    const target = targetRef.current;
    if (!contentId || !target) {
      abort(registrationErrorKey(null, 'site'));
      return;
    }
    // Callbacks handed to the form must never throw into it.
    try {
      const response = await postRegistration({
        contentId,
        ticketId: target.ticketId,
        promocodeId: target.promocodeId,
        answers,
        ...(reserveOffline ? { reserveOffline: true } : {}),
        channel: 'site',
        lang: language,
      });
      await handle(response, answers, reserveOffline);
    } catch {
      abort(registrationErrorKey(null, 'site'));
    }
  };

  const submitForm = async (answers: QuestionAnswer[]) => {
    setBusy(true);
    try {
      await submit(answers);
    } finally {
      setBusy(false);
    }
  };

  const confirmOffline = async () => {
    if (!offer) return;
    setReserving(true);
    try {
      await submit(offer.answers, true);
    } finally {
      setReserving(false);
    }
  };

  const start = async (target: SiteRegistrationTarget) => {
    targetRef.current = target;
    setTarget(target);
    needsRef.current = null;
    setServerErrors(null);
    setOffer(null);
    try {
      if (!optsRef.current.contentId) {
        abort('courseDetails.serverErrorTryAgain');
        return;
      }
      const result = await loadNeeds();
      if (!result.ok) {
        needsFailed(result.status);
        return;
      }
      const needs = result.needs;
      if (needsForm(needs)) {
        showForm(needs, null);
        return;
      }
      setBusy(true);
      try {
        await submit([]);
      } finally {
        setBusy(false);
      }
    } catch {
      abort(registrationErrorKey(null, 'site'));
    }
  };

  const cancelForm = () => {
    if (busy) return;
    close();
    optsRef.current.onAbort();
  };

  const closeOffer = () => {
    if (reserving) return;
    close();
    optsRef.current.onAbort();
  };

  const profileSaved = async () => {
    try {
      if (user) await refreshProfile(user.id, true);
    } catch {
      // The form already saved; a stale cached profile is not worth an error.
    }
  };

  // The form computes its shown fields on mount, so it waits for the catalog.
  // A catalog that failed to load is a failed check, not an empty catalog:
  // the form could not ask for a specialty at all.
  const catalogReady = specialties.data != null;
  const catalogFailed = specialties.data == null && specialties.error != null;
  useEffect(() => {
    if (formNeeds !== null && catalogFailed) needsFailed(500);
    // needsFailed only closes the form and reads refs; run once per failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formNeeds, catalogFailed]);
  const contentId = opts.contentId;

  const element = (
    <>
      <Modal isOpen={formNeeds !== null} onClose={cancelForm} title={t('registrationForm.title')} size="2xl">
        <div className="max-h-[70vh] overflow-y-auto -mx-6 px-6">
          {formNeeds && contentId && target && (catalogReady ? (
            <RegistrationForm
              channel="site"
              contentId={contentId}
              ticketId={target.ticketId}
              needs={formNeeds}
              specialties={specialties.data ?? []}
              serverErrors={serverErrors}
              submitLabel={target.isFree ? t('ticketCustomFields.finishRegistration') : t('ticketCustomFields.continue')}
              busy={busy}
              onProfileSaved={profileSaved}
              onSubmit={submitForm}
              onCancel={cancelForm}
            />
          ) : (
            <div role="status" aria-label={t('common.loading')} className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-900 rounded-full" />
            </div>
          ))}
        </div>
      </Modal>
      {offer && (
        <OfflineReserveModal
          amount={offer.amount}
          currency={offer.currency}
          instructions={offer.instructions}
          submitting={reserving}
          onConfirm={() => { void confirmOffline(); }}
          onClose={closeOffer}
        />
      )}
    </>
  );

  return { start, isOpen: formNeeds !== null || offer !== null, element };
}
